import { A as AttributeBindingBehavior } from './attribute-binding-behaviour.js';
import { O as Observable, b as ariaAttributeName, s as subscribeToAriaPropertyChanges, u as unsubscribeFromAriaPropertyChanges, D as DOM, c as ariaMixinProperties, A as AttachedBehaviorHTMLDirective } from './vivid-element.js';

class DelegateAriaBehavior {
  constructor(target, params) {
    this.target = target;
    this.source = null;
    this.bindingBehaviours = [];
    this.onSourceAriaPropertyChanged = (source, property) => {
      this.mitigateAccessibilityIssues(source);
      if (!this.forwardedProperties.has(property)) {
        return;
      }
      this.forwardPropertyToTarget(this.target, property, source[property]);
    };
    this.boundProperties = params.boundProperties;
    this.forwardedProperties = params.forwardedProperties;
    this.isMitigationDisabled = params.disableAccessibilityMitigation;
  }
  bind(source) {
    this.source = source;
    this.bindPropertiesToTarget(source, this.boundProperties, this.target);
    this.startForwardingPropertiesToTarget(
      source,
      this.forwardedProperties,
      this.target
    );
    this.mitigateAccessibilityIssues(source);
  }
  unbind() {
    if (this.source === null) {
      return;
    }
    this.releasePropertyBindings(this.source);
    this.stopForwardingPropertiesToTarget(this.source);
    this.source = null;
  }
  bindPropertiesToTarget(source, boundProperties, target) {
    for (const [property, binding] of Object.entries(boundProperties)) {
      const bindingFn = binding instanceof Function ? binding : () => binding;
      this.bindingBehaviours.push(
        new AttributeBindingBehavior(
          target,
          bindingFn,
          Observable.isVolatileBinding(bindingFn),
          ariaAttributeName(property)
        )
      );
    }
    source.$fastController.addBehaviors(this.bindingBehaviours);
  }
  releasePropertyBindings(source) {
    source.$fastController.removeBehaviors(this.bindingBehaviours);
    this.bindingBehaviours = [];
  }
  startForwardingPropertiesToTarget(source, delegatedProperties, target) {
    for (const key of delegatedProperties) {
      this.forwardPropertyToTarget(target, key, source[key]);
    }
    subscribeToAriaPropertyChanges(source, this.onSourceAriaPropertyChanged);
  }
  stopForwardingPropertiesToTarget(source) {
    unsubscribeFromAriaPropertyChanges(
      source,
      this.onSourceAriaPropertyChanged
    );
  }
  forwardPropertyToTarget(target, property, value) {
    DOM.setAttribute(target, ariaAttributeName(property), value);
  }
  /**
   * Sets role to presentation unless there are properties present that prohibit it.
   * Using role="presentation" will cause non-global states or properties to be ignored.
   * See: https://www.w3.org/TR/wai-aria-1.3/#conflict_resolution_presentation_none
   */
  mitigateAccessibilityIssues(source) {
    if (this.isMitigationDisabled) {
      return;
    }
    const hasProhibitedProperties = Boolean(
      source.ariaLabel || source.ariaBrailleLabel || source.ariaChecked
    );
    if (hasProhibitedProperties) {
      source.removeAttribute("role");
    } else {
      source.setAttribute("role", "presentation");
    }
  }
}

function delegateAria(boundProperties = {}, options = {}) {
  const forwardedProperties = new Set(
    (options.onlySpecified ? [] : ariaMixinProperties).filter((p) => p !== "role").filter((p) => !(p in boundProperties))
  );
  const disableAccessibilityMitigation = boundProperties.role instanceof Function;
  return new AttachedBehaviorHTMLDirective(
    "vvd-delegate-aria",
    DelegateAriaBehavior,
    {
      boundProperties,
      forwardedProperties,
      disableAccessibilityMitigation
    }
  );
}
const DelegatesAria = (Base) => {
  class DelegatesAriaElement extends Base {
    constructor() {
      super(...arguments);
      this._vividAriaBehaviour = "delegate";
    }
  }
  return DelegatesAriaElement;
};

export { DelegatesAria as D, delegateAria as d };
